import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkAPI } from '../../rootReducer';
import apiAsyncThunkCreator from '../../../services/apiAsyncThunkCreator';
import { CORE_SERVICES, HttpMethods } from '../../../services/api';
import { showErrorMessage } from '../../notifications';
import { DrawdownStatus } from '../drawdown/interfaces';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { isNetquestClient } from '../../../utils/clients';

import { DEFAULT_CURRENCY, IDrawdown } from './interfaces';

interface IFetchDrawdownsPayload {
    creditPoolId: string;
    page: number;
    limit: number;
    sortBy: string;
    sort: number;
    searchTerm?: string;
    status?: DrawdownStatus;
}

interface IFetchDrawdownsForExportPayload {
    creditPoolId: string;
    searchTerm?: string;
    status?: DrawdownStatus;
}

export interface IFetchDrawdownsResponse {
  docs: IDrawdown[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export const fetchDrawdowns: AsyncThunk<IFetchDrawdownsResponse, IFetchDrawdownsPayload, ThunkAPI> = createAsyncThunk(
  'drawdowns/fetch',
  async (data, thunk) => {
    try {
      const response = await apiAsyncThunkCreator<IFetchDrawdownsResponse, IFetchDrawdownsPayload>({
        endpoint: 'credit-pool/draw-down/table',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post
      })(data, thunk);
      return response;
    } catch (error) {
      thunk.dispatch(showErrorMessage(JSON.stringify(error)));
      return thunk.rejectWithValue(error);
    }
  }
);

export const fetchDrawdownsForExport: AsyncThunk<IFetchDrawdownsResponse, IFetchDrawdownsForExportPayload, ThunkAPI> = createAsyncThunk(
  'drawdowns/fetchForExport',
  async (data, thunk) => {
    try {
      const response = await apiAsyncThunkCreator<IFetchDrawdownsResponse, IFetchDrawdownsForExportPayload>({
        endpoint: 'credit-pool/draw-down/table/export',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post
      })(data, thunk);

      if (isFeatureEnabled(Features.CreditPoolExtension) && isNetquestClient() && 'docs' in response) {
        const state = thunk.getState();
        const currencySymbol = state.accounts?.hubspotAccounts?.account?.currencySymbol ?? DEFAULT_CURRENCY;
        response.docs?.forEach((drawdown: IDrawdown) => {
          drawdown.currency = currencySymbol;
        });
      }

      return response;
    } catch (error) {
      thunk.dispatch(showErrorMessage(JSON.stringify(error)));
      return thunk.rejectWithValue(error);
    }
  }
);
